import React, { useState, useEffect } from 'react';
import { Camera, Star, AlertTriangle, CheckCircle, X, ChevronLeft, ChevronRight, Lightbulb, Target, Ruler } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Curated training photos showing best practices
const CURATED_PHOTOS = {
  excellent: [
    {
      id: 'excellent_1',
      url: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=90',
      title: 'Perfect Window Measurement Photo',
      window_name: 'Living Room Window',
      quality_score: 95,
      tips: [
        'Full window frame visible in shot',
        'Credit card used as reference object',
        'Good lighting - no shadows on window',
        'Level horizon line',
        'High resolution image'
      ],
      category: 'excellent'
    },
    {
      id: 'excellent_2',
      url: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=90',
      title: 'Ideal Reference Object Placement',
      window_name: 'Bedroom Window',
      quality_score: 92,
      tips: [
        'Reference object (tape measure) clearly visible',
        'Entire window in frame',
        'Natural lighting shows true dimensions',
        'No obstructions blocking the view'
      ],
      category: 'excellent'
    }
  ],
  good_example: [
    {
      id: 'good_1',
      url: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=90',
      title: 'Good Photo with Minor Issues',
      window_name: 'Kitchen Window',
      quality_score: 78,
      tips: [
        'Window fully visible - good',
        'Consider adding a reference object',
        'Slightly dark - use flash or more light',
        'Frame edges could be more visible'
      ],
      category: 'good_example'
    },
    {
      id: 'good_2',
      url: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=90',
      title: 'Acceptable but Improvable',
      window_name: 'Office Window',
      quality_score: 75,
      tips: [
        'Good angle showing the window',
        'Add reference object for scale',
        'Ensure frame edges are sharp',
        'Better lighting would help'
      ],
      category: 'good_example'
    }
  ],
  common_mistakes: [
    {
      id: 'mistake_1',
      url: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=90',
      title: 'Too Dark / Poor Lighting',
      window_name: 'Example',
      quality_score: 35,
      tips: [
        'Turn on room lights or use flash',
        'Avoid backlighting from the window',
        'Take photo during daytime if possible',
        'Ensure details are clearly visible'
      ],
      category: 'common_mistake',
      isNegativeExample: true
    },
    {
      id: 'mistake_2',
      url: 'https://images.unsplash.com/photo-1600573472592-401b489a3cdc?w=800&q=90',
      title: 'Window Partially Cropped',
      window_name: 'Example',
      quality_score: 40,
      tips: [
        'Step back to capture entire window',
        'All four corners of frame should be visible',
        'Include some surrounding wall',
        'Use wide angle if needed'
      ],
      category: 'common_mistake',
      isNegativeExample: true
    },
    {
      id: 'mistake_3',
      url: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800&q=90',
      title: 'No Reference Object',
      window_name: 'Example',
      quality_score: 50,
      tips: [
        'Always include a reference object',
        'Use credit card, tape measure, or ruler',
        'Place object near the window frame',
        'Object should be clearly visible'
      ],
      category: 'common_mistake',
      isNegativeExample: true
    }
  ]
};

export const BestPhotosTrainingMode = ({ isOpen, onClose, token }) => {
  const [selectedCategory, setSelectedCategory] = useState('excellent');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [userPhotos, setUserPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUserPhotos();
    }
  }, [isOpen]);

  const fetchUserPhotos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/measurement-projects/training-photos?include_user_photos=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.excellent_photos) {
        setUserPhotos(response.data.excellent_photos.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch training photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'excellent', name: 'Excellent Photos', icon: Star, color: 'text-yellow-500', bgColor: 'bg-yellow-50' },
    { id: 'good_example', name: 'Good Examples', icon: CheckCircle, color: 'text-green-500', bgColor: 'bg-green-50' },
    { id: 'common_mistakes', name: 'Common Mistakes', icon: AlertTriangle, color: 'text-red-500', bgColor: 'bg-red-50' }
  ];

  const currentPhotos = CURATED_PHOTOS[selectedCategory] || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-blue-600" />
            Best Photos Training Guide
          </DialogTitle>
          <p className="text-sm text-neutral-500">
            Learn how to take perfect measurement photos for accurate quotes
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Category Tabs */}
          <div className="flex gap-2 mb-4">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === cat.id
                      ? `${cat.bgColor} ${cat.color}`
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.name}
                </button>
              );
            })}
          </div>

          {/* Photo Grid */}
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {currentPhotos.map((photo) => (
                <motion.div
                  key={photo.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`border rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow ${
                    photo.isNegativeExample ? 'border-red-200' : 'border-neutral-200'
                  }`}
                  onClick={() => setSelectedPhoto(photo)}
                >
                  <div className="relative aspect-video">
                    <img
                      src={photo.url}
                      alt={photo.title}
                      className="w-full h-full object-cover"
                    />
                    {photo.isNegativeExample && (
                      <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                        Avoid This
                      </div>
                    )}
                    <div className={`absolute bottom-2 left-2 px-2 py-1 rounded text-xs font-medium ${
                      photo.quality_score >= 80 
                        ? 'bg-green-500 text-white' 
                        : photo.quality_score >= 60 
                          ? 'bg-yellow-500 text-white'
                          : 'bg-red-500 text-white'
                    }`}>
                      Score: {photo.quality_score}%
                    </div>
                  </div>
                  <div className="p-3">
                    <h4 className="font-medium text-sm">{photo.title}</h4>
                    <p className="text-xs text-neutral-500 mt-1">{photo.window_name}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Tips Section */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                Pro Tips for Perfect Measurement Photos
              </h4>
              <ul className="mt-3 space-y-2 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <Target className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Always include a reference object (credit card, tape measure, or ruler) near the window frame</span>
                </li>
                <li className="flex items-start gap-2">
                  <Camera className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Ensure the entire window frame is visible in the photo, including all four corners</span>
                </li>
                <li className="flex items-start gap-2">
                  <Ruler className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Take photos straight-on (perpendicular to the window) to avoid distortion</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Use good lighting - avoid backlighting from the window by using flash or room lights</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Photo Detail Modal */}
        <AnimatePresence>
          {selectedPhoto && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedPhoto(null)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="relative">
                  <img
                    src={selectedPhoto.url}
                    alt={selectedPhoto.title}
                    className="w-full h-auto max-h-[50vh] object-contain bg-neutral-100"
                  />
                  <button
                    onClick={() => setSelectedPhoto(null)}
                    className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  {selectedPhoto.isNegativeExample && (
                    <div className="absolute top-2 left-2 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      Common Mistake - Avoid This
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">{selectedPhoto.title}</h3>
                      <p className="text-sm text-neutral-500">{selectedPhoto.window_name}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedPhoto.quality_score >= 80 
                        ? 'bg-green-100 text-green-700' 
                        : selectedPhoto.quality_score >= 60 
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                    }`}>
                      Quality Score: {selectedPhoto.quality_score}%
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">
                      {selectedPhoto.isNegativeExample ? 'What to Improve:' : 'What Makes This Great:'}
                    </h4>
                    <ul className="space-y-1">
                      {selectedPhoto.tips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-neutral-600">
                          {selectedPhoto.isNegativeExample 
                            ? <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                            : <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          }
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default BestPhotosTrainingMode;
